// The task queue: what this device owes the server, and the loop that pays it.
//
// The rules are **not here.** `core/src/queue.rs` decides which batches may go
// now (FIFO per document, concurrent across documents, a batch indivisible),
// what a server's answer means for one (`settleBatch`), what a teacher sees
// when one dies (`deadLetters`), and what the model forbids outright
// (`queueDefects`, `ruleViolations`), with no JavaScript twin (#125), so this
// file and its Android counterpart cannot drift apart. What is here is
// everything the core cannot see: a clock, a network, a database, and a
// promise somebody is awaiting.
//
// ── Nothing in this file imports Firebase ──────────────────────────────────
//
// Every collaborator arrives through `createQueue`'s options, so the queue
// runs in the `ui` jest project, which has no `firebase` mapper and needs no
// emulator (jest.config.cjs). A queue that imported `src/lib/firebase.js`
// would be testable only in `integration`, behind emulator ports.
// `src/data/writes.js` supplies the real clock, network and SDK.
//
// ── The contract awaited call sites get ────────────────────────────────────
//
//     The promise settles when the write is **acknowledged or durably owed,
//     whichever comes first.** It rejects only for a write that was refused
//     before it was owed.
//
// Online, the batch is dispatched immediately, the promise resolves on the
// server's acknowledgement, and a permanent refusal still rejects at the call
// site — so an await followed by a read of the server sees the write (#126).
//
// Offline (#108), the commit does not hang: the record is durable, the promise
// resolves, the drawer closes, and the chip carries the depth.
//
// A refusal that arrives *after* the promise resolved cannot reject it. It
// goes to `onRefused` instead, `src/data/writes.js`'s channel to the sync
// chip: by then the drawer that awaited it has closed.
//
// ── This queue dispatches while offline (#237, #277) ──────────────────────
//
// As `android/…/WriteQueue.kt` does. Withholding batches while
// `navigator.onLine` is false would cost two things:
//
//   * **#237.** `hasPendingWrites` is `hasLocalMutations || hasCommittedMutations`
//     and the only producer of the first is `mutationApplyToLocalView`, so a
//     write that never enters the SDK's mutation queue **cannot appear in any
//     `onSnapshot`**, which is what the snapshot read arm renders. A teacher
//     who saved offline would watch the drawer close on a row that had not
//     changed.
//   * **#277.** Nothing would restart the withheld batch when the network
//     returned: `drain()` runs when a session claims the queue (`writes.js`),
//     and `scheduleAt` is otherwise only reached from a `notBefore`, so the
//     batch would wait for the *next enqueue*.
//
// So: always dispatch. Firestore holds the mutation in its own IndexedDB queue,
// the cache shows it, the listeners fire, and the `commit()` promise settles
// whenever the network returns.
//
// **There is no connectivity signal in this file**, as on Android. `writes.js`
// watches `online` for one thing: a batch that was *answered* badly is sitting
// out a backoff, and a backoff measuring a dead network should not be waited
// out once the network is alive. That is a nudge, not a gate.
//
// ── A hang is not an answer, and it is not a failure either ────────────────
//
// `settle` in the core takes an outcome — acknowledged, transient, permanent —
// and an unrecognised one is treated as transient, spending a retry from the
// budget. That is right for an answer nobody understands and wrong for **no
// answer at all**, an awaited commit that never settles: spending the budget
// on silence would dead-letter a good write after a dozen rounds of a network
// being down.
//
// So `dispatchTimeoutMs` bounds **how long a caller waits, and nothing else.**
// It does not cancel the dispatch, spend a retry, or ask the core what silence
// means. The commit stays outstanding, the record stays `inflight` — which
// holds that document's FIFO until the answer lands — and it is settled
// whenever the answer arrives, in a continuation nobody is awaiting.
//
// It is not a park-and-retry: re-issuing a timed-out dispatch on a backoff,
// while dispatching offline, would hand the SDK a fresh mutation for the same
// document every few minutes for as long as the network was down. A hang is
// an undeclared offline state (#71), and offline does not cost a retry.
//
// ── Where the SDK's own queue fits, and why there is no barrier ────────────
//
// By default the SDK durably queues these writes as well, a second replayer
// under this one (#199). Dispatch over `firebase/firestore/lite`, which has no
// such queue, is `liteWriteFlag.js` (#410), off by default.
//
// **There is no `waitForPendingWrites` barrier before the first dispatch**
// (#126, #199), for two reasons:
//
//   1. **The SDK's own FIFO does its job.** A dispatch here is
//      `fsWriteBatch(db).commit()`, which appends to the mutation queue the SDK
//      is already holding its restored writes in, so our op is *behind* any
//      older op the SDK still holds, in one queue.
//
//      The four links, in `node_modules/@firebase/firestore/dist/`
//      `index.esm2017.js` at 4.7.3 — the `module` and `browser` entry, so what
//      Vite pre-bundles into `deps/firebase_firestore.js`.
//      `test/ui/sdkMutationOrder.test.jsx` asserts the four are still there:
//
//        * `batch.commit()` reaches `syncEngineWrite`, whose first act is
//          `localStoreWriteLocally` — so a commit enters the persistent
//          mutation queue rather than going straight out.
//        * `IndexedDbMutationQueue.addMutationBatch` takes the batch id from
//          **IndexedDB's own auto-increment key**: it writes an empty row to
//          get the key (`add({})`) and builds `new MutationBatch(thatKey, …)`.
//          So batch ids rise monotonically, per store, and the store is one
//          per origin.
//        * `getNextMutationBatchAfterBatchId(txn, id)` scans the
//          `userMutationsIndex` from `IDBKeyRange.lowerBound([uid, id + 1])`
//          and stops at the first row (`hardAssert(batchId >= id + 1)`), so it
//          answers with the **lowest** unacknowledged batch above the cursor.
//        * `fillWritePipeline` seeds its cursor from the last batch already in
//          the pipeline (or −1), then loops `localStoreGetNextMutationBatch`
//          → `addToWritePipeline`, advancing the cursor to each batch's id,
//          while `canAddToWritePipeline` — `canUseNetwork && pipeline < 10`.
//          `ensureWriteStream` is one stream.
//
//      So the pipeline is filled in strictly ascending batch id, which is the
//      order `addMutationBatch` ran, onto one stream. That the *backend*
//      applies one stream's writes in the order it receives them is assumed,
//      not tested here: the SDK behaves as though it does (`lastStreamToken`
//      chains, `lastAcknowledgedBatchId` is a single watermark). No test here
//      fails if the ordering stops holding; the bundle check only flags a
//      Firebase upgrade that moves the mechanism.
//   2. **A barrier would stall.** `waitForPendingWrites` does not settle while
//      any write is pending (#199), and dispatching offline makes "the SDK is
//      holding writes" the ordinary state. A barrier would time out on almost
//      every session that started with anything queued, and delay the first
//      dispatch, and with it the optimistic view #237 is about.
//
// The ordering argument depends on this loop dispatching while offline: the
// guarantee is that our op is an *append* to the SDK's queue, and a withheld
// batch is not. Lite dispatch (#410) removes the second replayer and the
// question with it. MMS has no offline editing, so there is no MMS behaviour
// to mirror here.
//
// The SDK also reconnects on its own: `BrowserConnectivityMonitor` registers
// `window.addEventListener('online')` and `RemoteStore` answers it with
// `restartNetwork()` — `disableNetworkInternal`, then `enableNetworkInternal`,
// which refills the write pipeline, as Android's `AndroidConnectivityMonitor`
// does.
//
// Double *delivery* is safe by the core's result: every conflict class is
// idempotent under double-apply, because an `Op` carries the document id
// minted once at enqueue rather than re-running a mutator that would mint a
// second one.
//
// That is about identity, not value. `serverTimestamp()` is rebuilt by the
// codec on every dispatch, so a second delivery stamps a later time. On
// `createdAt`, which `firestore.rules` pins with `createdAtUnchanged()`, a
// redelivery would be *refused*, classified permanent and dead-lettered
// (#232); `TRANSFORM_ENQUEUED_AT` resolves that field at enqueue instead.
//
// **Within one session a double delivery cannot happen.** Every path that
// re-dispatches goes through `settleWith`, which is only reached with an
// *answer*. The timeout does not produce one — it releases the caller and
// leaves the dispatch running — and an answer from the SDK means the SDK is no
// longer holding the batch. Across sessions and tabs it can, by way of
// `reclaimStranded` below.
//
// ── Owed counts as settled even when it is owed behind something else ──────
//
// #239. **A batch the core will not start yet is durably owed**: blocked by
// per-document FIFO behind a batch still in flight, or waiting out a backoff
// when some other enqueue runs a round before the timer fires. Holding either
// caller would leave a second save to a document that already had one in
// flight waiting for the *network* (`useTags.saveTag` twice, a student saved
// again, an attendance mark corrected).
//
// So every record that is `pending` and not being started this round is
// released. **Not a record that is `inflight`**: online, that caller should
// wait for the server, so that a permanent refusal rejects at a call site that
// can still act on it; the integration suite's await-then-read pattern depends
// on it.
//
// ── Reclaiming a batch a tab left in flight, and how "dead" is decided ─────
//
// #238. `send` patches a record to `inflight` before dispatching. A tab killed
// there leaves it that way: `ready_batches` returns a batch only when every op
// is `pending`, so it is never ready, never dispatched and never removed — and
// it is still *live*, so per-document FIFO blocks every later batch on its
// documents behind it (for the attendance batch, five documents).
//
// Android's `WriteQueue.recoverInheritedInflight()` resets every `inflight`
// record once, at the first round: one process owns the store, so nothing can
// be in flight in a process that has just started. **A browser has no such
// guarantee.** `coda-write-queue` is one IndexedDB database per origin and
// every tab opens it, so a sibling tab's live dispatch is visible here, and
// resetting it would make two replayers of us (#199) for a batch nobody lost.
// So the test is "this is not somebody's live work", in two halves:
//
//   * **Whose.** A record going `inflight` is stamped with `owner`, this
//     queue's session id, so our own live dispatch is never a candidate. The
//     stamp is what makes the check safe to run **every round** rather than
//     once: a sibling tab can die at any point in a session that stays open
//     for hours.
//   * **How long.** `staleInflightMs`. A tab offline for an hour holds its
//     records `inflight` for an hour, legitimately, so time since the
//     dispatch says nothing about whether the tab is alive. Instead a tab
//     **re-stamps `inflightAt` every `heartbeatMs`** while it has a dispatch
//     outstanding (`beat` below), and `staleInflightMs` means *nobody has
//     touched this record in five minutes*. `heartbeatMs` is a fifth of it, so
//     four wakes may be missed — and a hidden tab's `setTimeout` is clamped to
//     roughly one wake per minute under Chrome's intensive throttling. Without
//     the heartbeat, of two offline tabs the second would take the first's
//     live batch five minutes in and dispatch its own copy.
//
// A *frozen* tab — bfcache, or the browser's freeze on a backgrounded page —
// runs no timers for no bounded time, and no threshold tells it from a tab
// that is gone. Being wrong costs a **second delivery** of that batch, not a
// lost or doubled write: the op carries a document id minted once at enqueue
// and `createdAt` an instant resolved once and stored (#232), so the
// redelivery is byte-identical. The reclaim's own warning says so.
//
// Both deliveries are appends to one mutation queue: `src/lib/firebase.js`
// configures `persistentLocalCache({ tabManager:
// persistentMultipleTabManager() })`, one IndexedDB-backed mutation queue for
// the origin with a single primary tab owning the write stream. So the
// redelivery lands *after* whatever is already queued rather than racing it.
//
// ── The liveness test is a measurement, where the browser offers one ──────
//
// #263. A threshold is an inference from silence. `navigator.locks` knows: a
// tab takes an exclusive Web Lock named for its session id and holds it while
// alive, and **the browser releases it when the tab dies**, including a crash,
// a kill and a close. So the reclaim asks for the owner's lock with
// `ifAvailable`: granted means nobody holds it; refused means somebody does,
// and the record is not ours to take at any age.
//
//   * **A dead tab's batch is reclaimed at once** rather than in five minutes,
//     so an offline save followed by a reload does not read "1 pending" for
//     five minutes over a write the SDK already delivered.
//   * **A live tab's batch is never taken** while it holds its lock, however
//     throttled its timers: the lock is released on *destruction*, not on
//     idleness.
//
//     REASONED, not measured (#263): a bfcached document has not gone away,
//     so its lock should survive. Whether a browser releases it on *entering*
//     bfcache rather than on eviction is unobserved; at least one engine's
//     documented alternative is to refuse to bfcache a page holding a Web
//     Lock. Both are safe in the direction that matters.
//
// **It is a fallback, not a replacement.** `navigator.locks` needs a secure
// context and is absent in jsdom, so the threshold is the answer anywhere
// there is no lock manager — and the two paths print different warnings,
// because "the owner no longer holds its lock" and "nobody has stamped this in
// five minutes" are different evidence.
//
// A tab that never took a lock — an older build of this file live on the same
// origin — is indistinguishable from one that has died; the cost is the same
// byte-identical second delivery. The heartbeat stays: the threshold path
// reads it, and a queue cannot know every sibling tab has a lock manager.

/** Op and batch states. The core's vocabulary. */
const PENDING = 'pending';
const INFLIGHT = 'inflight';
const DEAD = 'dead';

/**
 * The state a record wears **after** it has left this queue's durable store,
 * while `confirmed()` is still handing it to readers (#410).
 *
 * Not one of the core's three: the core is never asked about it, no record is
 * ever stored under it, and nothing here retries or refuses it. It exists so
 * that a record out of `confirmed()` cannot be mistaken for a live one if it
 * ever reaches code expecting `records()` — `isLiveWrite` below and
 * `overlay.js`'s `isLive` are both affirmative, so both answer false for it.
 */
export const CONFIRMED = 'confirmed';

/**
 * May this record's write still land at the server?
 *
 * Affirmative — `pending` or `inflight` — rather than `!== 'dead'`, which is
 * the same rule `overlay.js` states for the fold and for the same reason: a
 * fourth state added here should make a caller decide rather than silently
 * join one of the two groups.
 *
 * **Exported for `writes.hasLiveWriteTo`** (#345). `overlay.js` cannot import
 * this module — its header says why — so it keeps its own copy of the two
 * strings, pinned against real records by `test/ui/overlay.test.jsx`.
 */
export function isLiveWrite(record) {
  return record?.state === PENDING || record?.state === INFLIGHT;
}

/** What a dispatch attempt came back with. The first three are the core's. */
export const OUTCOME = {
  acknowledged: 'acknowledged',
  transient: 'transient',
  permanent: 'permanent',
  /**
   * No answer at all, within the time we were prepared to wait.
   *
   * **Ours, not the core's**, and it never crosses the boundary — see the
   * header. The core is asked what a server's answer means; silence is not one.
   */
  unreachable: 'unreachable',
};

const DEFAULTS = {
  // Twelve attempts with the backoff below is a bit over an hour of a server
  // failing in a way that might yet work. Past that, a dead letter the teacher
  // can see beats a queue that never drains (#162, #125).
  budget: 12,
  baseBackoffMs: 1000,
  maxBackoffMs: 5 * 60 * 1000,
  // Long enough that an ordinary slow commit is not mistaken for a wedge, short
  // enough that a form does not feel hung.
  //
  // **It bounds the caller's wait, not the dispatch.** Nothing is cancelled or
  // re-issued when it fires (#277): the commit stays with the SDK and
  // settles whenever the network returns. So being wrong here costs a form that
  // closed a little early, not a retry and not a second delivery.
  dispatchTimeoutMs: 10000,
  /**
   * How long a record may sit `inflight` before another tab may take it back.
   *
   * **Measured against the heartbeat below, not against `dispatchTimeoutMs`.**
   * This loop dispatches offline (#237, #277), so a record stays `inflight`
   * for as long as the outage lasts; `beat` re-stamps `inflightAt` every
   * `heartbeatMs` for every dispatch this session still has out. Five minutes
   * is four missed wakes, and a hidden tab's timers are clamped to roughly one
   * wake per minute under Chrome's intensive throttling.
   *
   * It bounds the throttled case and **does not bound the frozen one** — a
   * bfcached or frozen tab runs no timers for no bounded time. See the header
   * for what being wrong about that costs.
   *
   * **The fallback rather than the rule (#263).** Where the browser has
   * `navigator.locks`, liveness is measured and this number decides nothing
   * about reclaiming — it is only how long the queue waits before asking
   * again. Anywhere there is no lock manager it is the whole answer.
   */
  staleInflightMs: 5 * 60 * 1000,
  /**
   * How often a tab re-stamps the records it still has a dispatch out for.
   *
   * A fifth of `staleInflightMs`, so the threshold above survives four missed
   * wakes. A heartbeat slower than the threshold would make every live offline
   * tab look stranded to its siblings.
   */
  heartbeatMs: 60 * 1000,
  /**
   * How long an **acknowledged** batch is still handed to a reader, while the
   * read path waits for the server's own echo of it (#410).
   *
   * ── What this number is for ───────────────────────────────────────────────
   *
   * Over `firebase/firestore/lite` a write enters no mutation queue: the full
   * SDK's listener learns about it only when the **watch stream** delivers it
   * back, one round trip after `commit()` resolved. Were the record removed
   * from this store the instant it was acknowledged, nothing would hold the
   * teacher's own change between those two moments and the row would revert
   * to its old value — the flicker `npm run test:lite` reproduces.
   *
   * The full SDK closes the same gap with `hasCommittedMutations`: its cache
   * keeps a committed mutation applied until the echo arrives. `confirmed()`
   * below does that for our queue, handing the batch to the overlay after it
   * has left the durable store, and `overlay.js` drops it once the store's
   * receipt for the scope is newer than the acknowledgement.
   *
   * ── So why a duration at all ─────────────────────────────────────────────
   *
   * The receipt is the *only* release, and a scope nothing is listening to
   * never gets a newer one, so a collection with no mounted provider would
   * hold its confirmed batches for the life of the tab. Thirty seconds is far
   * longer than a watch round trip and far shorter than a session.
   *
   * **Being wrong here is not a lost write**: the batch is gone from the
   * durable store before this clock starts. Too short shows the server's older
   * row for the rest of the round trip (the flicker); too long shows a value
   * the server already agrees with, and masks another device's concurrent
   * change to the same document for as long as it lasts.
   */
  confirmedHoldMs: 30 * 1000,
};

/**
 * The table above, readable from outside.
 *
 * One consumer: `test/ui/harnessBudgets.test.jsx` asserts that the
 * integration harness's `QUEUE_SETTLE_BUDGET_MS` is not shorter than
 * `dispatchTimeoutMs` (#404), reading the number from here rather than
 * re-typing `10000`.
 *
 * Read-only by convention; nothing here freezes it.
 */
export const QUEUE_DEFAULTS = DEFAULTS;

/**
 * This queue's name for itself, for the life of one `createQueue`.
 *
 * It has one job: be different from the id every *other* live tab on this
 * origin stamps on a record, so a reclaim can tell somebody else's live
 * dispatch from nobody's. It is never shown and only compared for equality,
 * so `Math.random` is enough.
 */
const newSessionId = () => `s-${Math.random().toString(36).slice(2, 12)}`;

/**
 * The Web Lock a session holds for as long as it is alive (#263).
 *
 * One per session id, so the question `reclaimStranded` asks — "is the tab
 * that stamped this record still running?" — is answered by the browser
 * rather than inferred from a stamp. The name is derived from the `owner`
 * already on the record, so nothing new is stored.
 */
const LOCK_PREFIX = 'coda-write-queue/';
const lockNameFor = (owner) => `${LOCK_PREFIX}${owner}`;

/**
 * The stamp `send` leaves, undone.
 *
 * Applied by every arm that leaves the record behind rather than removing it,
 * so a parked or dead record can never be mistaken for one a tab is still
 * working on. A record that survives `send` is by definition not in flight.
 */
const NOT_INFLIGHT = { owner: null, inflightAt: 0 };

/**
 * A queue.
 *
 * @param store     `src/data/queueStore.js` — durability.
 * @param loadCore  `src/lib/core.js` — the rules.
 * @param dispatch  `(record) => Promise<{ outcome, error }>` — the network.
 *
 * **There is no connectivity option and that is deliberate** — this queue
 * dispatches either way, because a write withheld from the SDK cannot appear in
 * any snapshot listener and would vanish off the teacher's own screen (#237).
 * `writes.js` watches `online` for a rising-edge nudge instead. There is no
 * ordering `barrier` either; the header says why.
 *
 * @param subjectForPath  `(path) => string|null` — the teacher's noun for a
 *                  collection. The vocabulary stays in `writes.js`, which
 *                  already owns that table; the core takes it as a join table
 *                  because a boundary cannot carry a closure.
 * @param onRefused `({ code, message, subject, reason }) => void` — a write
 *                  that died, for the chip.
 * @param onChange  `(change) => void` — the depth moved. `change` is
 *                  `{ paths }` naming every document the transition touched,
 *                  or **`null`** where this file cannot enumerate them. See
 *                  `announce` below: `null` means *ask again about everything*,
 *                  and a listener that treats it as "nothing moved" is #237.
 */
export function createQueue(options) {
  const {
    store,
    loadCore,
    dispatch,
    subjectForPath = () => null,
    onRefused = () => {},
    onChange = () => {},
    // Called once per batch, when it reaches a terminal state and only then —
    // never when a batch is parked. `writes.js` clears #167's durable record
    // here rather than when the caller's promise settles: the promise resolves
    // as soon as the write is *owed*, so clearing on it would take a queued
    // write out of the chip's count while it is still queued (#162).
    onSettled = () => {},
    // Called once per batch, when its record has reached the store and before
    // anything is dispatched (#585). `writes.js` keeps a copy of the batch in
    // the synchronous outstanding-write note until this says the queue's own
    // record exists; see `admit`.
    onDurable = () => {},
    now = () => Date.now(),
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (t) => clearTimeout(t),
    budget = DEFAULTS.budget,
    baseBackoffMs = DEFAULTS.baseBackoffMs,
    maxBackoffMs = DEFAULTS.maxBackoffMs,
    dispatchTimeoutMs = DEFAULTS.dispatchTimeoutMs,
    staleInflightMs = DEFAULTS.staleInflightMs,
    heartbeatMs = DEFAULTS.heartbeatMs,
    confirmedHoldMs = DEFAULTS.confirmedHoldMs,
    sessionId = newSessionId(),
    /**
     * The browser's `LockManager`, or null where there is none (#263).
     *
     * Injected like every other collaborator so the `ui` project can exercise
     * it: jsdom has no `navigator.locks`. `globalThis` rather than `navigator`
     * because the `functions` project loads this module too, and has no
     * `navigator`.
     */
    locks = globalThis.navigator?.locks ?? null,
    /**
     * Does the dispatcher put this device's writes in order itself? (#423)
     *
     * `() => false` — the default, the full-SDK dispatcher — means it does:
     * `writes.js` hands each batch to the full SDK, which appends it to one
     * mutation queue and sends that queue on one stream, so the server sees
     * enqueue order across documents however many batches this loop has out at
     * once. The core's per-document rule is then enough.
     *
     * `true` means it does not, and the core's **studio-order** rule decides
     * instead: one batch per studio at a time, each after every earlier one has
     * landed, died or been discarded. `writes.js` passes
     * `liteWritesEnabled` — a lite `commit()` is one independent REST call, and
     * two of them, or one parked and retried on its own clock, land in
     * whichever order the network delivers. `core/src/queue.rs` has the
     * argument and the cost.
     *
     * A function, asked every round, because the answer it mirrors is asked
     * per dispatch: `liteWriteFlag.js` reads its flag each time, so a batch is
     * ordered by the rule of the transport that will actually send it.
     */
    inStudioOrder = () => false,
  } = options;

  // The promise a call site is holding, by batch. **In memory only** (#167):
  // the record outlives the tab and the promise does not. A batch replayed in
  // a later session has no entry here, settles nobody, and reports through
  // `onRefused` if it dies, which `outstandingWrites.js` notices.
  const waiting = new Map();
  let seq = null;
  // Guards *choosing* what to dispatch, which must not overlap itself. It does
  // not cover waiting for a dispatch — see `scheduleOnce`.
  let scheduling = false;
  let again = false;
  /** The scheduling round in progress, so a concurrent caller can join it. */
  let currentSchedule = null;

  /**
   * Batches the server has taken, kept only until a reader stops needing them.
   *
   * **In memory, never durable, and outside `store`.** The durable record's
   * meaning is *this is owed*, and an acknowledged batch is not owed — writing
   * it back would give `depth()`, `reclaimStranded` and every replayer a
   * record to act on. This is the fact the full SDK carries as
   * `hasCommittedMutations`: the server has it and our own listener has not
   * been told yet. It is lost on reload, which is correct: a reload re-reads
   * from the server, and there is no echo left to wait for.
   *
   * Keyed by batch id; each value is the record as it was, plus `confirmedAt`.
   * See `confirmedHoldMs` above for the bound and `overlay.js` for the release.
   */
  const confirmed = new Map();

  /** Drop what the hold has outlived. Lazy — there is no timer for this. */
  function pruneConfirmed() {
    if (!confirmed.size) return;
    const cutoff = now() - confirmedHoldMs;
    [...confirmed.entries()].forEach(([batchId, record]) => {
      if (record.confirmedAt <= cutoff) confirmed.delete(batchId);
    });
  }

  /**
   * Hold an acknowledged batch for the readers, on its way out of the store.
   *
   * `state` is overwritten rather than left as `inflight`, so a record from
   * here can never be mistaken for a live one: `isLiveWrite` above and
   * `overlay.js`'s `isLive` both name their two states affirmatively.
   */
  function rememberConfirmed(record) {
    confirmed.set(record.batchId, {
      ...record, state: CONFIRMED, confirmedAt: now(),
    });
    pruneConfirmed();
  }

  // ── Every timer this queue arms, so a session ending can disarm them ──────
  //
  // Two kinds get armed: the retry timer for a parked batch, and the
  // per-dispatch timeout inside `send`, which stays armed for the whole
  // timeout if the dispatch never answers. A test that walks away from such a
  // write would leave it to fire, park and re-arm — a retry loop under
  // whatever runs next, and a worker that will not exit.
  const liveTimers = new Set();
  const arm = (fn, ms) => {
    const timerId = setTimer(() => {
      liveTimers.delete(timerId);
      fn();
    }, ms);
    liveTimers.add(timerId);
    return timerId;
  };
  const disarm = (timerId) => {
    if (!timerId) return;
    liveTimers.delete(timerId);
    clearTimer(timerId);
  };
  /**
   * Batch ids whose dispatch this session still has out.
   *
   * **Its lifetime is the dispatch's, not `send`'s.** A dispatch that has not
   * answered within `dispatchTimeoutMs` releases its caller and returns from
   * `send` while the commit is still with the SDK, and for the whole of that
   * time this batch is ours: it must not be picked by another round of
   * `scheduleOnce`, nor reclaimed by `reclaimStranded`. Both read this set.
   */
  const inflight = new Set();
  /** Batch ids already reclaimed, so the warning below is said once each. */
  const reclaimed = new Set();
  /** The `send` promises themselves, so `drain` knows when nothing more starts. */
  const pending = new Set();
  /** The heartbeat timer, armed only while something is in flight. */
  let heartbeat = null;
  /**
   * Bumped by `reset` and by `endSession`, so a dispatch answering afterwards
   * writes nothing.
   *
   * `endSession` is the sign-out — `reset` is the test seam (#283). A detached
   * dispatch is one nobody is awaiting: without this, a commit answering after
   * a sign-out would clear the next teacher's outstanding-write note and
   * report a settlement for a session that has ended.
   */
  let generation = 0;
  /**
   * Whether the session this queue was working for has ended (#298).
   *
   * `endSession` disarms every timer and puts this session's `inflight`
   * records back to `pending`, but cannot cancel a scheduling round already
   * in the promise queue, which can finish and re-arm the backoff timer just
   * disarmed. The harmful case is a batch due at that instant, on a tab where
   * `signOut(auth)` has resolved and `SyncProvider`'s effect has not yet told
   * `writes.js` that nobody is signed in: the dispatch guard sees a studio
   * that still matches, the batch reaches a client with no credentials,
   * `firestore.rules` refuses it, and the queue dead-letters a good write
   * (#282).
   *
   * ── Why this cannot wedge the queue ───────────────────────────────────────
   *
   * Every path that can start a dispatch on a session's behalf clears it
   * first:
   *
   *   * `drain()` clears it, and `drain()` is what `startWriteQueue` calls on
   *     every mount — including `startWriteQueue(db, null)`, the signed-out
   *     call `SyncProvider` makes one effect after the sign-out. So the queue
   *     resumes on the pass that closes the window, with no teacher and no
   *     write required. `wake()` and `retry()` reach it through `drain` too.
   *   * `enqueue` clears it, so a write made in a session that never signed in
   *     is not held behind a flag from the last one.
   *
   * What stays gated is a timer, a round already in flight, and **a
   * reconnect**. None of those is a session claiming this queue.
   *
   * ── A reconnect is not a claim ────────────────────────────────────────────
   *
   * `drain()` is a session claiming the queue and clears this; `nudge()` is
   * the device saying the network is back and does not. If `writes.js`'s
   * `online` listener cleared it, a reconnect inside the window above would
   * re-arm the timers and let a due batch through to credentials that had
   * just gone.
   */
  let ended = false;
  let timer = null;
  /** When `timer` is set to fire, so `scheduleAt` can keep the earliest. */
  let timerAt = 0;

  /**
   * The next enqueue order for this device.
   *
   * Resumed from the store rather than started at zero, because `seq` is the
   * order a device enqueued things in and a reload must not restart it — a
   * fresh batch numbered below a queued one would claim to have come first and
   * jump the document's FIFO.
   *
   * ── The initialisation is shared ──────────────────────────────────────────
   *
   * Two overlapping `enqueue` calls (two writes to one document in the same
   * tick, an ordinary thing for a form to do) that each read the store while
   * `seq` is null would both assign from the same read, and **both batches
   * would get seq 1**. `readyBatches` blocks a batch only behind one whose seq
   * is strictly lower, so both would be ready at once and the document's FIFO
   * would be off. `writeQueue.test.jsx`'s "two batches on one document go in
   * order" pins it.
   *
   * Sharing the read means one store lookup decides the baseline, and the
   * increment below is synchronous after the await, so two callers cannot
   * come away with the same number.
   */
  let seqReady = null;
  async function nextSeq() {
    if (seq === null) {
      if (!seqReady) {
        seqReady = store.all().then((rows) => {
          if (seq === null) seq = rows.reduce((max, r) => Math.max(max, r.seq), 0);
        });
      }
      await seqReady;
    }
    seq += 1;
    return seq;
  }

  // ── What moved, where this file can say (#389) ─────────────────────────────
  //
  // The signal carries no diff of what is owed (`writes.js` says why: the
  // overlay re-derives the whole queue on every read). What is passed here is
  // a hint about what a listener may **skip**: the documents this transition
  // touched. A listener that is woken re-derives everything.
  //
  // Naming a path that did not move costs one wasted read; failing to name one
  // that did is a screen that does not show a teacher their own write (#237).
  // So every arm below either enumerates the records it moved or passes
  // nothing — and nothing means **unknown**, never "empty".
  //
  // An empty or absent list is normalised to `null` here rather than forwarded
  // as a set nothing matches: a record always has ops, so an empty list is a
  // caller's bug, and the failure it would cause is the silent one.
  function announce(records) {
    const list = Array.isArray(records) ? records : [records];
    const paths = [];
    for (const r of list) {
      if (!r || !Array.isArray(r.ops)) return announceUnknown();
      for (const op of r.ops) if (op?.path) paths.push(op.path);
    }
    if (!paths.length) return announceUnknown();
    return fire({ paths });
  }

  /** The transition moved something this file cannot enumerate. Wake everyone. */
  function announceUnknown() {
    return fire(null);
  }

  function fire(change) {
    try {
      onChange(change);
    } catch (err) {
      console.error('[queue] change listener threw:', err);
    }
  }

  // A batch's ops, as the core wants them: one row per document, carrying the
  // batch's state and attempt count, since a batch settles as one.
  function opsOf(record) {
    return record.ops.map((op) => ({
      id: op.id,
      batchId: record.batchId,
      seq: record.seq,
      path: op.path,
      kind: op.kind,
      state: record.state,
      attempts: record.attempts,
      reason: record.reason || '',
    }));
  }

  const allOps = (records) => records.flatMap(opsOf);

  function settleWaiter(batchId, error) {
    const waiter = waiting.get(batchId);
    if (!waiter) return;
    waiting.delete(batchId);
    if (error) waiter.reject(error);
    else waiter.resolve();
  }

  /**
   * Refuse a write the model forbids, before anything durable happens.
   *
   * **`queueDefects` only, and `ruleViolations` deliberately not.**
   * `queueDefects` asks "is this a well-formed operation" — a kind it does not
   * know, an `increment`, a batch that contradicts itself, a
   * `serverTimestamp` that arrived already resolved. No legitimate call site
   * produces one, so it is a hard refusal: a write stored now is replayed
   * later with nobody to read a console line.
   *
   * `ruleViolations` asks "does this collection's conflict class permit this
   * write", and as an enqueue refusal it would break every create this app
   * makes. `test/core/wholeDocumentSets.test.js` walks `src/data/`, resolves
   * each write's collection against `PER_FIELD_MERGE` in
   * `core/src/reconcile.rs`, and pins both what it classified and what it
   * could not attribute (#204).
   *
   * **Every one of them is a create**, where writing the document whole is
   * what creating it is. Two writes that are not creates have shapes of their
   * own:
   *
   *   * `useTags.saveTag` writes a *set*, `studentIds`, which has a class of
   *     its own (`memberSet`, #288), so `saveTag` is a merge like everything
   *     else.
   *   * `useSettings.saveStudio` is a merging set naming the patched fields,
   *     with the whole record sent only on a create — which the app can tell
   *     and the core cannot (#337). Writing the composed record whole would
   *     lose one of two devices' offline changes to two different settings.
   *
   * The core has no view of the document, so it cannot tell a create from an
   * overwrite. The argument is at `Defect::WHOLE_DOCUMENT_SET` in
   * `core/src/reconcile.rs`: a fourth class cannot reach a create, and a
   * create flag on the op is a boolean every platform computes offline, wrong
   * in both directions — one turns the check off exactly where it is needed;
   * the other refuses a teacher's first student with a conflict class quoted
   * at them.
   *
   * So `ruleViolations` is reported and never refused (#204), and
   * `Defect::explanation` says it is advisory in the sentence itself.
   */
  function refuseDefects(core, ops, fields) {
    const defects = core.queueDefects(ops, fields);
    if (!defects.length) return;
    // ── The sentence is the core's, and the code is still here (#205, #295) ──
    //
    // The sentence comes from `explainDefect`, beside the constant it explains
    // in `core/src/queue.rs`; this file adds the two things a platform owns:
    // which op it was, and the greppable code, kept so a developer chasing one
    // across files can grep for it.
    const detail = defects.map((d) => `${core.explainDefect(d.code, d.detail || '')} [op ${
      d.opId || '(the batch)'}, ${d.code}]`);
    const error = new Error(`[queue] refusing a write the model forbids: ${detail.join('; ')}`);
    error.code = 'coda/queue-defect';
    error.defects = defects;
    throw error;
  }

  /**
   * Make a batch durable: the shared half of `enqueue` and `restore`.
   *
   * Refuses a malformed batch before anything is stored, stores it, tells the
   * readers, and then tells `onDurable` — **after** the put has committed,
   * because that is the moment `writes.js` may drop the copy it kept in the
   * outstanding-write note (#585). Before it, that copy is the only record of
   * the write that would survive the page closing.
   */
  async function admit({ batchId, ops, fields, subject, enqueuedAt }) {
    const core = await loadCore();
    const record = {
      batchId,
      seq: await nextSeq(),
      state: PENDING,
      attempts: 0,
      reason: '',
      notBefore: 0,
      subject,
      enqueuedAt,
      ops,
      fields,
    };
    refuseDefects(core, opsOf(record), fields);
    await store.put(record);
    report(onDurable, batchId);
    announce(record);
    return record;
  }

  /**
   * Take on a batch.
   *
   * Resolves per the contract in the header. The record is durable before this
   * returns anything, and the caller's promise cannot resolve before it is.
   */
  async function enqueue({ batchId, ops, fields, subject = null }) {
    // A write is a session. #298's flag is cleared here as well as in `drain`
    // so that a queue whose last session ended cannot hold a *new* teacher's
    // write behind a flag that is about the previous one.
    ended = false;
    await admit({ batchId, ops, fields, subject, enqueuedAt: now() });
    const promise = new Promise((resolve, reject) => {
      waiting.set(batchId, { resolve, reject });
    });
    // Not awaited: the caller's promise is the one that matters and the drain
    // settles it. `drain` never throws for an ordinary write failure, so a
    // rejection here is a bug in the loop and is reported. `schedule`, not
    // `drain`: this only has to *start* the work. Waiting for quiescence would
    // make one caller's promise wait on every other caller's dispatch.
    schedule().catch((err) => console.error('[queue] scheduling failed:', err));
    return promise;
  }

  /**
   * Run another round at `at`, or sooner if something already wanted sooner.
   *
   * **One timer, and the earliest request wins.** There are two callers: a
   * backoff, and a record that becomes reclaimable when `staleInflightMs` has
   * passed since a dead tab last stamped it. A round asks for both, and
   * last-wins would silently throw away whichever was asked for first.
   *
   * Waking early is free: the round re-reads the store, finds nothing due, and
   * re-arms for whatever is still outstanding. Waking late is the defect.
   */
  function scheduleAt(at) {
    // #298. A queue whose session has ended arms nothing; in a test runner an
    // armed timer is a worker that will not exit. Every caller goes through
    // here. A timer that is already armed stays armed and finds `ended` at the
    // top of the round it starts.
    if (ended) return;
    if (timer !== null && timerAt <= at) return;
    disarm(timer);
    timerAt = at;
    timer = arm(() => {
      timer = null;
      schedule().catch((err) => console.error('[queue] scheduled drain failed:', err));
    }, Math.max(0, at - now()));
  }

  // ── Holding this session's lock, and asking about somebody else's (#263) ──
  //
  // Two halves of one mechanism, and the only two places `locks` is touched.

  /** Resolves once this session's lock is held, or at once if there is none. */
  let holdingLock = null;
  /** Releases it. Called by `reset` only — a live tab holds its lock. */
  let releaseLock = null;

  /**
   * Take this session's lock, once, and hold it until the tab dies.
   *
   * Awaited before a record is stamped `inflight`, so a sibling asking about
   * the `owner` on that record cannot find the lock unheld while we are alive;
   * the other order leaves a window in which our own live dispatch looks
   * abandoned.
   *
   * `locks.request` resolves when the callback's promise settles, so the
   * callback returns one that settles only when `releaseLock` is called. The
   * grant itself is signalled separately — that is what is awaited.
   */
  function holdSessionLock() {
    if (!locks) return Promise.resolve(null);
    if (holdingLock) return holdingLock;
    holdingLock = new Promise((granted) => {
      // Without a held lock this session's records are indistinguishable from
      // a dead tab's, so a sibling may reclaim one early. That is a redelivery
      // rather than a loss — see the header — so every failure below is
      // reported and resolved rather than retried or thrown: a queue that
      // could not take a lock must still dispatch.
      const failed = (err) => {
        console.error('[queue] could not hold this session\'s lock:', err);
        granted(false);
      };
      // The `try` covers the *call* alone. A `LockManager` that exists and
      // refuses — an insecure context — throws from here rather than
      // rejecting, and wrapping the handlers as well would be #184's shape:
      // a catch written for one failure quietly covering every bug below it.
      try {
        // Two arguments rather than a trailing `.catch`, for the same reason.
        locks.request(lockNameFor(sessionId), () => new Promise((release) => {
          releaseLock = release;
          granted(true);
        })).then(() => {}, failed);
      } catch (err) {
        failed(err);
      }
    });
    return holdingLock;
  }

  /**
   * Is the tab that stamped this record gone?
   *
   * `true` it is gone, `false` it is alive, **`null` this cannot be measured
   * here** — which is a third answer rather than a pessimistic second one, and
   * the caller prints a different sentence for it. A lock that can be taken is
   * a lock nobody holds; the browser releases it when the tab dies.
   */
  function ownerIsGone(owner) {
    if (!locks || !owner) return Promise.resolve(null);
    const unanswerable = (err) => {
      // `null` is "could not measure": it sends this record back to the
      // threshold rather than reclaiming it on a failure to ask.
      console.error('[queue] could not ask whether a batch\'s owner is alive:', err);
      return null;
    };
    // As in `holdSessionLock`: the `try` is around the call, not the handlers.
    try {
      return locks.request(
        lockNameFor(owner),
        { ifAvailable: true, mode: 'exclusive' },
        // Granted (a lock object) means nobody was holding it. Returning
        // releases it again immediately, which is the whole of what we wanted:
        // the answer, not the lock.
        (lock) => lock !== null,
      ).then((gone) => gone, unanswerable);
    } catch (err) {
      return Promise.resolve(unanswerable(err));
    }
  }

  /**
   * Keep every record this session still has a dispatch out for looking alive.
   *
   * `reclaimStranded` takes a batch back from a tab it judges dead, and the
   * only evidence it has is `inflightAt`. This loop dispatches offline, so a
   * tab holds its records for as long as the outage lasts, and it says so
   * periodically rather than leaving a sibling to infer it from a stamp that
   * stopped moving.
   *
   * Armed only while something is in flight, and re-armed from its own
   * completion rather than as an interval, so a slow store cannot stack them.
   */
  function beat() {
    if (heartbeat || !inflight.size) return;
    heartbeat = arm(() => {
      heartbeat = null;
      if (!inflight.size) return;

      const at = now();
      // Two-argument `then`: the rejection handler is for the store's own
      // failure. A trailing `.catch` here would also swallow a bug in the
      // re-arm below, which is the shape #184 was.
      Promise.all([...inflight].map((batchId) => store.patch(batchId, { inflightAt: at }))).then(
        () => beat(),
        (err) => {
          // A stamp that did not land is a record that may be reclaimed early
          // by a sibling tab. That is a redelivery, not a loss — see the
          // header — so it is reported rather than retried.
          console.error('[queue] re-stamping an outstanding dispatch failed:', err);
          beat();
        },
      );
    }, heartbeatMs);
  }

  /**
   * Stop the heartbeat when there is nothing left to stamp.
   *
   * A queue holding nothing in flight arms no timers at all — in a browser a
   * spare one is a wake a hidden tab did not need, and in a test runner it is
   * a worker that will not exit (see `liveTimers`).
   */
  function stopBeatIfIdle() {
    if (inflight.size) return;
    disarm(heartbeat);
    heartbeat = null;
  }

  /** The race lost, rather than an answer that happens to be falsy. */
  const TIMED_OUT = Symbol('timed out');

  /**
   * One attempt at the network, which never rejects.
   *
   * Separated from `send` because it has to outlive the caller's wait: the
   * timeout below does not cancel it, and a promise that could still reject
   * after nobody is awaiting it is an unhandled rejection.
   */
  function attemptDispatch(record) {
    return Promise.resolve().then(() => dispatch(record)).then(
      (answer) => answer || { outcome: OUTCOME.transient },
      // A `dispatch` that threw rather than answering. Transient, because that
      // is the outcome that cannot destroy anything — the same argument the
      // core makes for an unrecognised outcome.
      (err) => ({ outcome: OUTCOME.transient, error: err }),
    );
  }

  /**
   * Hand one batch to the network and wait a bounded time for an answer.
   *
   * The bound is here rather than inside `dispatch` so that every supplier of
   * a network — the real SDK, a test double — gets it.
   *
   * **Returning is not settling.** A dispatch that has not answered inside
   * `dispatchTimeoutMs` leaves this function still running (#277), with the
   * record still `inflight` and the caller released. See the header.
   */
  async function send(core, record) {
    const dispatchedIn = generation;
    // Stamped with who and when, which is all `reclaimStranded` has to go on:
    // without them an `inflight` record is indistinguishable from a sibling
    // tab's live dispatch. Cleared on every arm below that leaves the record
    // behind, so a parked record never looks stranded.
    // The lock is held before the stamp: the `owner` written below names the
    // lock a sibling will ask about, and a record stamped while it is unheld
    // looks abandoned to anybody who looks in that window (#263). A no-op
    // where there is no lock manager.
    await holdSessionLock();
    await store.patch(record.batchId, {
      state: INFLIGHT, owner: sessionId, inflightAt: now(),
    });
    // `pending` → `inflight`. The overlay folds the two identically (see
    // `liveOpsByPath` in overlay.js), so a listener this wakes computes the
    // value it already holds and #379's identity check discards it. It is
    // announced anyway: the chip counts states, the overlay's sensitivity is
    // not this file's to decide, and the cost is one read per touched
    // collection.
    announce(record);

    const attempt = attemptDispatch(record);
    beat();

    let answer;
    let timeout;
    try {
      answer = await Promise.race([
        attempt,
        new Promise((resolve) => {
          timeout = arm(() => resolve(TIMED_OUT), dispatchTimeoutMs);
        }),
      ]);
    } finally {
      disarm(timeout);
    }

    if (answer === TIMED_OUT) {
      // No answer inside the time we were prepared to *wait*, which is not no
      // answer. The write is durable, with the SDK, and owed — so the caller is
      // released and this round ends so other documents can go. The record
      // stays `inflight`, holding this document's FIFO until the answer lands,
      // and `attempt` is still running.
      settleWaiter(record.batchId);
      announce(record);
      attempt.then(
        (late) => settleWith(core, record, late, dispatchedIn),
        // `attemptDispatch` does not reject, so this is a bug in this file
        // rather than a failed write — reported, and never mistaken for one.
        (err) => {
          inflight.delete(record.batchId);
          stopBeatIfIdle();
          console.error('[queue] a dispatch attempt rejected, which it cannot:', err);
        },
      ).then(
        () => {
          // Something settled, so a batch blocked behind it on its own document
          // may now be ready — and offline, this is the only thing that ever
          // restarts the loop.
          schedule().catch((err) => console.error('[queue] rescheduling failed:', err));
        },
        (err) => {
          inflight.delete(record.batchId);
          stopBeatIfIdle();
          console.error('[queue] settling a late answer failed:', err);
        },
      );
      return;
    }

    await settleWith(core, record, answer, dispatchedIn);
  }

  /**
   * What the server (or a thrown dispatch) said, applied to the record.
   *
   * Reached only with an *answer*. The timeout does not produce one, which is
   * what makes "the SDK still holds this batch" and "we may dispatch it again"
   * mutually exclusive within a session — see the header.
   */
  async function settleWith(core, record, answer, dispatchedIn) {
    inflight.delete(record.batchId);
    stopBeatIfIdle();
    // A queue whose session has ended is not this one. A late answer arriving
    // after a sign-out must not clear the next teacher's note or report a
    // settlement for a session that is over — `endSession` bumps the
    // generation, having already put this record back to `pending`.
    if (dispatchedIn !== generation) return;
    const { outcome, error } = answer || { outcome: OUTCOME.transient };

    if (outcome === OUTCOME.unreachable) {
      // A dispatch that *says* it could not reach anybody, which is different
      // from one that has not answered yet. Parked with the attempt count
      // untouched: silence is not an answer, so the core is not asked what it
      // means, and an undeclared offline state costs no more than a declared
      // one.
      //
      // ── Who produces it, by kind (#282, #410) ───────────────────────────
      //
      //   * **Waiting on a teacher.** `writes.js` — a batch whose
      //     `users/{uid}/…` prefix is not the signed-in studio's, held until
      //     that teacher comes back. Only a sign-in can change this answer, so
      //     only `wake()` releases it.
      //   * **Waiting on a handle.** `writes.js` with no full-SDK Firestore,
      //     and `src/data/liteDispatch.js` with no `FirebaseApp` yet, or a
      //     `liteFirestore()` that threw.
      //   * **Waiting on the network.** `liteDispatch.js`'s
      //     `classifyCommitError` — a lite `commit()` that rejected without
      //     anybody judging the write. This kind is why `nudge()` releases the
      //     park as well as `wake()`: the full SDK leaves an offline promise
      //     pending, so on it a batch with no network stays `inflight` and
      //     never reaches here.
      //
      // `liteDispatch.js` failing to *build* the batch is not a park: it is
      // `transient` (#423), because no event could release it, and under the
      // studio-order rule a park nothing releases holds every later write to
      // the studio behind it.
      //
      // ── Why the park escalates (#284) ──────────────────────────────────────
      //
      // A flat park at `baseBackoffMs`, with a `dispatch` that always answers
      // `unreachable`, is a dispatch a second with `attempts` never moving: a
      // batch held for a teacher who may never sign in again on this browser
      // would be ~86,400 full `store.all()` reads a day, for the life of the
      // tab.
      //
      // The retry budget is not spent: silence and "not for you" are not
      // refusals, and #125's terminal state is for writes the server answered.
      // So the escalation is a *separate* clock carried on the record: it
      // doubles from `baseBackoffMs` to `maxBackoffMs` and is cleared the moment
      // any other answer arrives, or when the thing it was waiting for has
      // changed — a sign-in, said by `wake()` from `SyncContext`, or the
      // network coming back, said by `nudge()` from `writes.js`'s `online`
      // listener.
      const held = Math.min(
        record.unreachableFor ? record.unreachableFor * 2 : baseBackoffMs,
        maxBackoffMs,
      );
      await store.patch(record.batchId, {
        state: PENDING, notBefore: now() + held, unreachableFor: held, ...NOT_INFLIGHT,
      });
      settleWaiter(record.batchId);
      announce(record);
      scheduleAt(now() + held);
      return;
    }

    const d = core.settleBatch(
      record.batchId, record.attempts, outcome, budget, baseBackoffMs, maxBackoffMs,
    );

    if (d.state === 'done') {
      // **Before the record leaves the store, and only for an acknowledgement**
      // (#410). The condition is on the outcome rather than on `done`: what
      // this holds is *the server has this write*, and only one outcome says
      // so. A `discard` never reaches here.
      if (outcome === OUTCOME.acknowledged) rememberConfirmed(record);
      await store.remove(record.batchId);
      settleWaiter(record.batchId);
      // The record is gone from the store by now, so the paths come off the
      // copy still in hand. The overlay stops folding these ops, and the stored
      // row the snapshot wrote is what shows.
      report(onSettled, record.batchId, 'done');
      announce(record);
      return;
    }

    if (d.state === PENDING) {
      // `unreachableFor: 0` — somebody answered, so the escalating park above
      // starts again from the bottom if it is ever reached again.
      await store.patch(record.batchId, {
        state: PENDING,
        attempts: d.attempts,
        notBefore: now() + d.retryAfterMs,
        unreachableFor: 0,
        ...NOT_INFLIGHT,
      });
      // Owed, not lost — so the call site is released rather than left holding
      // a promise across an hour of backoff.
      settleWaiter(record.batchId);
      announce(record);
      scheduleAt(now() + d.retryAfterMs);
      return;
    }

    // Dead, by refusal or by exhaustion, kept apart all the way to the teacher:
    // one is "the server will never take this", the other "we stopped
    // trying".
    await store.patch(record.batchId, {
      state: DEAD, attempts: d.attempts, reason: d.reason, unreachableFor: 0, ...NOT_INFLIGHT,
    });
    const refusal = {
      code: error?.code || d.reason,
      message: error?.message || `the write was not delivered (${d.reason})`,
      subject: record.subject,
      reason: d.reason,
      batchId: record.batchId,
    };
    settleWaiter(record.batchId, error || new Error(refusal.message));
    report(onRefused, refusal);
    report(onSettled, record.batchId, 'dead');
    announce(record);
  }

  // A listener that throws must not stop the next step of the queue, and must
  // not turn a refused write into a second, unrelated failure (as in
  // `writes.js`'s own subscribers).
  function report(listener, ...args) {
    try {
      listener(...args);
    } catch (err) {
      console.error('[queue] listener threw:', err);
    }
  }

  /**
   * Put back a batch that has been `inflight` longer than anybody could hold it.
   *
   * #238; the header has the argument. Three conditions:
   *
   *   * `owner !== sessionId` — not our own live dispatch. This is what makes
   *     the check safe to run every round rather than once, the difference
   *     from Android's `recoverInheritedInflight`.
   *   * `!inflight.has(...)` — the same thing said from memory rather than
   *     from the store, and **not redundant**: `send` stamps the record one
   *     awaited store round-trip after this set was added to, so a round that
   *     runs in between reads a record whose stamp has not landed yet.
   *   * old enough that a tab running this loop would have re-stamped it — see
   *     `beat`. A heartbeat rather than `dispatchTimeoutMs`, because a live
   *     tab holds a record `inflight` for as long as an outage lasts.
   *
   * A record with no `inflightAt` reads as `0` and is stale by the same rule:
   * only an older build writes an `inflight` record without a stamp, and that
   * is not this session.
   *
   * `attempts` is left alone. A reclaimed batch that goes on failing still
   * runs out the budget it had already spent; resetting it would make a batch
   * that keeps being inherited immortal.
   */
  async function reclaimStranded(records) {
    const at = now();
    const inherited = records.filter((r) => r.state === INFLIGHT
      && r.owner !== sessionId
      && !inflight.has(r.batchId));
    if (!inherited.length) return records;

    // ── Measured where the browser can measure it (#263) ────────────────────
    //
    // `null` from `ownerIsGone` is "this cannot be measured here", a third
    // answer and not a quiet `false`: it sends a record back to the threshold
    // below, and it is why the two warnings differ. One request per record
    // rather than per owner — a round has one or two inherited records, and
    // `ifAvailable` returns without waiting.
    const verdicts = await Promise.all(inherited.map((r) => ownerIsGone(r.owner)));
    const gone = new Map(inherited.map((r, i) => [r.batchId, verdicts[i]]));

    const stranded = inherited.filter((r) => (gone.get(r.batchId) === null
      // No lock manager: the threshold.
      ? at - (r.inflightAt || 0) >= staleInflightMs
      // A lock manager: its answer, at any age. A record stamped a second ago
      // by a tab that has since died is reclaimed now rather than in five
      // minutes, and one stamped an hour ago by a tab that is merely frozen is
      // not reclaimed at all.
      : gone.get(r.batchId)));

    // ── A record that is not stale *yet* gets a timer ────────────────────────
    //
    // Save offline, reload (#278): the new tab inherits a record stamped
    // seconds ago, so nothing is reclaimed — correctly — and a round happens
    // only on an enqueue, on `online`, on a backoff and at startup, none of
    // which is "five minutes from now". Without a timer the chip would read "1
    // pending" over a write the SDK had already delivered, until the teacher
    // saved something else. `notBefore` has the same shape below.
    //
    // **Still armed when the owner was measured alive**: nothing here is woken
    // by a lock being released, so a sibling that dies while this tab sits idle
    // would otherwise not be noticed until the next save. On that path the
    // timer is a re-*ask* rather than a reclaim, and it is `staleInflightMs`
    // **from now** rather than from the stamp — a frozen tab holds its lock
    // while stamping nothing, so its stamp goes arbitrarily stale while the
    // answer stays "alive", and waking at a time already past would spin.
    const waking = inherited
      .filter((r) => !stranded.includes(r))
      .map((r) => (gone.get(r.batchId) === null
        ? (r.inflightAt || 0) + staleInflightMs
        : at + staleInflightMs));
    if (waking.length) scheduleAt(Math.min(...waking));

    if (!stranded.length) return records;

    for (const r of stranded) {
      if (reclaimed.has(r.batchId)) continue;
      reclaimed.add(r.batchId);
      // Says what it saw, offers both readings, and names the one it cannot
      // rule out, because a person acts on this. The duration is printed only
      // when there is one: an unstamped record would otherwise print
      // arithmetic on a default of `0` as a measurement. The cost if this is
      // wrong is the same on both paths and said on both: a second delivery,
      // never a lost or doubled write.
      const cost = 'this batch goes out twice. That cannot lose or double a write — the op '
        + 'carries a document id minted once at enqueue and `createdAt` an instant resolved '
        + 'once (#232), and both deliveries append to the one mutation queue the SDK keeps '
        + 'for this origin.';
      console.warn(gone.get(r.batchId)
        // ── Measured (#263) ────────────────────────────────────────────────
        //
        // A different sentence, because it is different evidence: the browser
        // released a lock, which it does when a tab dies. It still names what
        // it cannot rule out — a tab that never took a lock looks identical to
        // one that has died.
        ? `[queue] batch ${r.batchId} is being dispatched again (#238): the session that had `
          + `it in flight (${r.owner || 'unnamed'}) no longer holds its Web Lock, and the `
          + 'browser releases that lock when a tab dies. That is a measurement rather than a '
          + 'timeout, so a tab that is merely frozen or throttled is not mistaken for a dead '
          + 'one. The case it cannot tell apart is a tab that never took a lock at all — a '
          + `build older than this check, or a context with no \`navigator.locks\`. If it is `
          + `that, ${cost}`
        // ── Inferred, where there is no lock manager ───────────────────────
        : `[queue] batch ${r.batchId} ${r.inflightAt
          ? `was last stamped ${at - r.inflightAt} ms ago`
          : 'carries no stamp at all, so it predates this check'}, past the `
          + `${staleInflightMs} ms a tab running this loop lets one go unstamped — it re-stamps `
          + `every ${heartbeatMs} ms while a dispatch is out — so it is being dispatched again `
          + '(#238). **There is no `navigator.locks` in this context, so liveness could not be '
          + 'measured and this is a threshold rather than an observation.** Two cases it cannot '
          + 'tell apart: the tab that owned it has gone, or it is alive and frozen — a suspended '
          + `or bfcached tab runs no timers, so it cannot stamp either. If it is the second, `
          + `${cost}`);
    }

    await Promise.all(stranded.map((r) => store.patch(
      r.batchId, { state: PENDING, notBefore: 0, ...NOT_INFLIGHT },
    )));
    // Every record it reclaimed, so the union of their documents is exactly
    // what moved.
    announce(stranded);

    // Handed back rather than re-read: a second `store.all()` would be a
    // round-trip spent asking for what we just wrote. New objects, never
    // mutations of the store's own, so a memory store that hands out live
    // references is unharmed.
    const back = new Set(stranded.map((r) => r.batchId));
    return records.map((r) => (back.has(r.batchId)
      ? { ...r, state: PENDING, notBefore: 0, ...NOT_INFLIGHT }
      : r));
  }

  /**
   * Decide what may go now, and start it.
   *
   * ── Why deciding and waiting are separate ────────────────────────────────
   *
   * Awaiting its own dispatches — `await Promise.all(due.map(send))` — inside
   * a single-flight drain would throw away "concurrent across documents",
   * half of the core's serialisation rule: a write to `students/x1` is
   * dispatched and the drain sits awaiting it; a write to `ledgerEntries/x1`
   * enqueued a moment later sees a drain running, raises `again`, and is not
   * handed to the SDK until the first is acknowledged. Offline that is
   * minutes. `syncRefusal.test.jsx`'s "counts a second one" pins it: it
   * refuses two writes and expects the chip to say two, which needs both to
   * have reached the SDK.
   *
   * So the lock is around **choosing** batches, which is short and must not
   * overlap itself, and there is no lock around waiting for them. A dispatch
   * that settles kicks another round, which is how a batch blocked behind one
   * on its own document gets its turn.
   */
  async function scheduleOnce() {
    // #298, the cheap half: an ended session reads no store, patches nothing
    // and arms nothing. The half that closes the race is inside the dispatch
    // loop below, for a round that got past here before the sign-out.
    if (ended) return;
    const core = await loadCore();
    const records = await reclaimStranded(await store.all());
    if (!records.length) return;
    const byId = new Map(records.map((r) => [r.batchId, r]));
    // Dead records are handed to the core along with the live ones rather than
    // filtered out here: `readyBatches` already skips a batch whose ops are not
    // all pending, and `causalGaps` needs them present to report what a dead
    // batch left behind.
    //
    // **Which of the core's two rules, asked every round (#423).** Per
    // document suits a dispatcher that hands each batch to the SDK's own
    // ordered write stream; a transport that sends each batch as its own
    // request needs the studio-order rule, or a charge and the invoice request
    // after it can land the wrong way round. See `inStudioOrder` above.
    const ops = allOps(records);
    const ready = (inStudioOrder() ? core.readyBatchesInStudioOrder(ops) : core.readyBatches(ops))
      // A batch whose dispatch is already running. Its record says `inflight`
      // too, but that patch is a store round-trip behind this read, and picking
      // one twice would issue the same write twice for no reason.
      .filter((batch) => !inflight.has(batch.batchId));

    const at = now();
    const due = [];
    let earliest = Infinity;
    for (const batch of ready) {
      const record = byId.get(batch.batchId);
      if (!record) continue;
      if ((record.notBefore || 0) > at) earliest = Math.min(earliest, record.notBefore);
      else due.push(record);
    }

    // ── Owed counts as settled, including when it is owed behind something
    //    else (#239) ─────────────────────────────────────────────────────────
    //
    // Every record that is durably stored and not going out this round:
    // blocked by FIFO behind one still in flight, or waiting out a backoff.
    // Both are "durably owed", which the contract at the top of this file says
    // settles the caller.
    //
    // **Not a record that is `inflight`**, by either reading of it: the
    // store's state, and this session's in-memory set for the round-trip where
    // the two disagree. Online, that caller waits for the server, so a
    // permanent refusal rejects where something can still be done about it.
    //
    // Computed over `records` rather than `ready`: a FIFO-blocked batch is not
    // in `ready` at all.
    const starting = new Set(due.map((r) => r.batchId));
    records
      .filter((r) => r.state === PENDING
        && !starting.has(r.batchId)
        && !inflight.has(r.batchId))
      .forEach((r) => settleWaiter(r.batchId));

    if (!due.length) {
      if (earliest < Infinity) scheduleAt(earliest);
      return;
    }

    // **There is no connectivity check here** (#237, #277; the header has the
    // argument). #108's fix lives in `send`'s timeout: the caller is released
    // either way, and the *write* is not withheld.

    // `ready` guarantees no two of these share a document (the core asserts
    // it), so starting them together needs no second lock.
    for (const record of due) {
      // #298, the check that closes it. Everything above has awaited storage at
      // least twice, so a session can end between the top of this function and
      // here: `endSession` returns, a round already in the promise queue comes
      // back, and this batch is due at that instant. `break`, because `ended`
      // cannot become false again inside one synchronous loop; the records stay
      // durable, pending, and owed.
      if (ended) break;
      inflight.add(record.batchId);
      // **`inflight` is not cleared here**: `send` may return while its
      // dispatch is still with the SDK, and `settleWith` owns the removal.
      // Clearing it on `send`'s completion would let the next round pick a
      // batch this session already has out.
      const running = send(core, record).then(
        () => {},
        (err) => {
          inflight.delete(record.batchId);
          stopBeatIfIdle();
          console.error('[queue] dispatch failed outside its own handling:', err);
        },
      ).then(() => {
        pending.delete(running);
        // Something settled, so a batch that was blocked behind it on its own
        // document may now be ready.
        schedule().catch((err) => console.error('[queue] rescheduling failed:', err));
      });
      pending.add(running);
    }
  }

  /**
   * Choose work, without two choosers overlapping.
   *
   * **A concurrent caller joins the round in progress rather than returning at
   * once**, which is what makes `drain`'s promise mean anything. A caller that
   * returned at once would believe the choosing finished before it started,
   * and `drain` would walk out of its loop with a batch nobody has looked at —
   * so `retry()` would report a drained queue over a batch still in it.
   */
  function schedule() {
    if (scheduling) {
      again = true;
      return currentSchedule;
    }
    scheduling = true;
    currentSchedule = (async () => {
      try {
        do {
          again = false;
          await scheduleOnce();
        } while (again);
      } finally {
        scheduling = false;
      }
    })();
    return currentSchedule;
  }

  /**
   * The loop itself, with no opinion about #298's flag.
   *
   * Split out so its two callers' meanings can be told apart — see `drain` and
   * `nudge` below.
   */
  async function drainLoop() {
    await schedule();
    while (pending.size) {
      await Promise.all([...pending]);
      await schedule();
    }
  }

  /**
   * **A session claiming this queue**, then work the queue until nothing more
   * can be started.
   *
   * `retry()` and the tests need more than "the work was scheduled": a drain
   * that returned once the work was merely *chosen* would let `retry` report
   * having drained a queue whose batch had not been handed to anybody.
   *
   * A dispatch that has not answered inside `dispatchTimeoutMs` is still
   * outstanding when this returns — with the SDK, waiting for a network — and
   * the continuation that will settle it is not awaited here: that would be a
   * drain that does not return until the tab has signal, #108 again. So
   * *quiescent* means **nothing more can be started**, not **nothing is in
   * the air**.
   *
   * Safe to call concurrently. Waiting on a set of promises is idempotent, and
   * the choosing underneath is what holds the lock.
   *
   * Clearing `ended` here rather than in each caller is the resumption #298's
   * flag depends on: `startWriteQueue` drains on every mount, and `wake()` and
   * `retry()` drain, so "somebody is asking this queue to work" has one
   * definition. See `ended`.
   *
   * ── The reconnect listener is not one of these ────────────────────────────
   *
   * The browser firing `online` is not a session claiming anything. A
   * reconnect that cleared the flag after `endSession()` and before
   * `SyncProvider` has told `writes.js` that nobody is signed in would re-arm
   * the timers and dispatch a due batch to a client whose credentials had just
   * gone (#282, #298). `nudge()` is that other meaning, with its own name. The
   * default is the claiming one: a caller who forgets which they wanted gets a
   * queue that resumes rather than one that will not.
   */
  async function drain() {
    ended = false;
    await drainLoop();
  }

  /**
   * Drop the `unreachable` park from every record carrying one.
   *
   * Shared by `wake()` and `nudge()`: both say *the thing this batch was
   * waiting for may have changed*, and the park is not a backoff anybody
   * should wait out once it has. It clears `notBefore` and the
   * `unreachableFor` clock together, so the escalation starts again from
   * `baseBackoffMs` if the answer is still no.
   *
   * **Never touches a transient backoff**: a server that refused us a moment
   * ago has not changed its mind because of a sign-in or a reconnect. Only the
   * state that means *nobody judged this write* is cleared.
   */
  async function releaseUnreachable() {
    const records = await store.all();
    const held = records.filter((r) => r.state === PENDING && (r.unreachableFor || 0) > 0);
    if (!held.length) return;
    await Promise.all(held.map((r) => store.patch(
      r.batchId, { notBefore: 0, unreachableFor: 0 },
    )));
    announce(held);
  }

  /**
   * **Not a session claiming this queue** — ask it to have another look.
   *
   * For the reconnect listener, and anything else that is a fact about the
   * *device* rather than about a teacher: a backoff measuring a dead network
   * should not be waited out once the network is alive. It leaves `ended` as
   * it found it, so a queue whose session is over stays quiet, and
   * `schedule()`'s own `if (ended) return` makes this a no-op there.
   *
   * ── It releases the `unreachable` park (#410) ──────────────────────────────
   *
   * On the full SDK `unreachable` has **no network producer**: the SDK holds
   * an unsendable write in its own mutation queue and leaves the promise
   * pending, so an offline batch sits `inflight` rather than parked, and only
   * a sign-in (#282, #284) can change the answer — `wake()`.
   *
   * `dispatchViaLite` gives it one: lite's `commit()` is one REST call that
   * rejects promptly when there is no network, `classifyCommitError` maps that
   * to `unreachable`, and the park doubles to `maxBackoffMs`. Without this
   * release, a write saved offline would go out up to five minutes after the
   * network returns.
   *
   * **`nudge` rather than `wake` from the listener**, which is why the release
   * is factored out: `wake()` drains, and `drain()` means *a session is
   * claiming this queue* — a reconnect in the window after a sign-out would
   * re-arm the timers and dispatch to credentials that had gone (#298).
   *
   * **The release is skipped when `ended`**, so this stays a complete no-op on
   * a queue whose session is over; otherwise a reconnect between a sign-out
   * and the next `startWriteQueue` would reset the escalation on a batch held
   * for a teacher who is not here.
   */
  async function nudge() {
    if (!ended) await releaseUnreachable();
    await drainLoop();
  }

  return {
    enqueue,
    drain,
    nudge,

    /**
     * Put back a batch an earlier page noted and never stored (#585).
     *
     * `writes.js` writes its outstanding-write note synchronously, *then*
     * awaits this queue's IndexedDB put. A page that goes away between the two
     * — a reload, a navigation, a closed tab — leaves a note and no batch. The
     * note carries a copy of the batch until the put lands, and this is how
     * that copy comes back.
     *
     * **Durable on return, and nothing more.** No caller is waiting — the one
     * that was died with its page — so no waiter is made, and nothing is
     * scheduled: whoever restores is expected to `drain()` once it is done,
     * the act that says a session is claiming this queue (#282, #298).
     * Dispatching from here would send a batch before `writes.js` knows whose
     * session this is.
     *
     * `enqueuedAt` is the note's own time, so the record says when the teacher
     * saved rather than when a later page found it. The batch keeps its id;
     * its `createdAt` instant is in its fields already (`TRANSFORM.enqueuedAt`),
     * so a redelivery carries the same one (#232).
     */
    async restore({ batchId, ops, fields, subject = null, enqueuedAt = now() }) {
      return admit({ batchId, ops, fields, subject, enqueuedAt });
    },

    /** How many batches are owed. The chip's number. */
    async depth() {
      const records = await store.all();
      return records.filter((r) => r.state !== DEAD).length;
    },

    /**
     * Every document path this queue will still write to.
     *
     * For `SyncContext`'s #167 check: is a durable note left over from an
     * earlier session describing work this queue has in hand, or work that
     * fell into the gap between the note and the record? `waitForPendingWrites`
     * alone cannot answer it (#278): "the SDK has nothing outstanding" is not
     * "the server answered and nobody heard".
     *
     * A **dead** batch is not owed: nothing will send it again without a
     * teacher saying so, so a note covering it is reported as unconfirmed
     * rather than hidden behind a record that is going nowhere.
     */
    async owedPaths() {
      const records = await store.all();
      return new Set(records
        .filter((r) => r.state !== DEAD)
        .flatMap((r) => r.ops.map((o) => o.path)));
    },

    /** The list a teacher sees, with why each one died. */
    async deadLetters() {
      const core = await loadCore();
      const records = await store.all();
      const paths = [...new Set(records.flatMap((r) => r.ops.map((o) => o.path)))];
      const subjects = paths.map((path) => ({ path, subject: subjectForPath(path) || '' }));
      return core.deadLetters(allOps(records), subjects);
    },

    /**
     * Conflict-class rules the queued writes do not satisfy.
     *
     * Reported, never refused — see `refuseDefects` for why, and
     * `test/core/wholeDocumentSets.test.js` for the call sites. It makes the
     * check the core already knows how to make reachable from the app rather
     * than only from a Rust test.
     *
     * A caller that renders it should render `core.explainDefect(code, detail)`
     * rather than the code (#295). The sentence for each lives beside its
     * constant in `core/src/queue.rs`, and `wholeDocumentSet` — most of what
     * this can return — says *advisory* in itself, because a caller may
     * legitimately meet it and go ahead (#204).
     */
    async ruleViolations() {
      const core = await loadCore();
      const records = await store.all();
      return records.flatMap((record) =>
        opsOf(record).flatMap((op) =>
          core.ruleViolations(
            op.id, op.path, op.kind, record.fields.filter((f) => f.opId === op.id),
          )));
    },

    /**
     * Documents whose history has a hole a dead batch left in it.
     *
     * Reported rather than resolved, which is the core's own choice of words:
     * it can see that the order was broken and cannot see whether it mattered.
     */
    async causalGaps() {
      const core = await loadCore();
      return core.causalGaps(allOps(await store.all()));
    },

    /** Put a dead batch back in the queue, at the front of its own documents. */
    async retry(batchId) {
      await store.patch(batchId, {
        state: PENDING, attempts: 0, reason: '', notBefore: 0, unreachableFor: 0,
      });
      // A batch id and no record: the store is patched without reading it back,
      // so the documents cannot be named from here, and a read for the sake of
      // a hint is not worth it on a button press.
      announceUnknown();
      await drain();
    },

    /** Give up on a dead batch. The teacher's decision, never ours. */
    async discard(batchId) {
      await store.remove(batchId);
      report(onSettled, batchId, 'discarded');
      // Removed by id, same as `retry` above: the record is gone and was never
      // in hand.
      announceUnknown();
    },

    /**
     * The queue's own view of what it holds.
     *
     * **Not a test seam** (#207): `src/data/overlay.js` calls it on every read
     * to fold unsent writes over stored documents, so its shape is a contract:
     *
     *   * every record this queue holds, `dead` ones included — the caller
     *     decides what is live, and the overlay names `pending` and `inflight`
     *     affirmatively rather than excluding `dead`;
     *   * `seq` is the enqueue order the server will see;
     *   * `ops` carry `path` and `kind`, and `fields` carry `opId` so a batch's
     *     documents keep their fields apart.
     *
     * It reads storage and nothing else — **no network, no drain, no
     * barrier** — so a read stays off the path #239 released a caller from.
     * Making this await a dispatch would put that hang on every render.
     */
    async records() {
      return store.all();
    },

    /**
     * Batches the server has taken that our own listener has not echoed yet.
     *
     * The second half of `records()`' contract with `src/data/overlay.js`
     * (#410). These records are **not owed**: they have been acknowledged,
     * removed from the durable store, and their callers settled. They are the
     * fact the full SDK spells `hasCommittedMutations` — *the server has this
     * and the screen does not know yet* — which over lite dispatch nothing else
     * holds, because a lite `commit()` enters no mutation queue for the SDK's
     * cache to apply.
     *
     * Each record is as `records()` would have given it, with two differences:
     *
     *   * `state` is `'confirmed'`, which `isLiveWrite` and the overlay's
     *     `isLive` both answer false for, so one of these can never be counted
     *     as owed by code that was handed it by mistake;
     *   * `confirmedAt` is this queue's clock at the acknowledgement, which is
     *     what the overlay compares against the store's receipt to decide the
     *     echo has arrived.
     *
     * Bounded by `confirmedHoldMs` and pruned on the way past — there is no
     * timer, because nothing has to happen when one expires.
     */
    async confirmed() {
      pruneConfirmed();
      return [...confirmed.values()];
    },

    /**
     * Something a held batch was waiting for may have changed.
     *
     * **The event half of #284.** A batch parked `unreachable` is waiting on a
     * condition the queue cannot see — the teacher whose studio it writes into
     * signing back in, or a Firestore handle arriving. Without this, the
     * escalating park would make a batch that becomes sendable wait out a
     * five-minute timer.
     *
     * `SyncContext` calls this when the auth state resolves to a uid, the event
     * that can change the #282 answer. It clears the *unreachable* park only,
     * never a transient backoff.
     *
     * `nudge()` releases the same park on a reconnect, because the lite
     * dispatcher gives `unreachable` a network producer. What is only
     * `wake()`'s is the *claim*: this drains, so it says a session is working
     * this queue, and a reconnect does not (#298).
     */
    async wake() {
      await releaseUnreachable();
      await drain();
    },

    /**
     * The session this queue has been working for is over.
     *
     * **This is not `reset()`** (#283). A write still owed at sign-out can be
     * stranded, parked for that teacher's return, or block the sign-out. This
     * parks it, as `writes.js`'s dispatch guard does (#282): **nothing durable
     * is destroyed.** `reset()` clears the store and is a test seam; calling it
     * from a sign-out would delete the previous teacher's unsent work on a
     * shared laptop.
     *
     * What ends is this session's *hold* on the work:
     *
     *   * **Every record this tab had `inflight` goes back to `pending`**
     *     (#283). The queue is a singleton that outlives the sign-out, so a
     *     record it still owned would otherwise sit `inflight` forever: nothing
     *     would retry it, dead-letter it, or tell anybody. `reclaimStranded`
     *     cannot reach it either, because it asks `owner !== sessionId` and
     *     this is the same session.
     *   * **The generation is bumped**, so a dispatch already with the SDK
     *     cannot come back and settle a record under the next teacher, or clear
     *     a note that is now theirs. See `settleWith`.
     *   * **Every caller still awaiting one is released, not rejected**: its
     *     write is still durably owed.
     *   * **Nothing dispatches again until a session claims the queue**
     *     (#298). A scheduling round already in the promise queue can finish
     *     after the timers are disarmed, re-arm one, and — if a batch is due at
     *     that instant — dispatch it to a client whose credentials have just
     *     gone. `ended` stops that; see its declaration for why it cannot wedge
     *     the queue.
     *
     * It does **not** drain: the work is owed to a studio this tab is no longer
     * signed in to, and dispatching it now is the wrong-credentials refusal
     * #282 is about. The next session decides: `writes.js` holds it if the uid
     * differs, and sends it if it does not.
     */
    async endSession() {
      // Set first, before anything awaits, so a round already in the promise
      // queue sees it at whichever of its two checks it reaches next (#298).
      ended = true;
      generation += 1;
      [...liveTimers].forEach(disarm);
      timer = null;
      timerAt = 0;
      heartbeat = null;
      const mine = new Set(inflight);
      inflight.clear();
      reclaimed.clear();
      pending.clear();
      // The next teacher must not read this one's confirmed rows off a shared
      // laptop. Nothing durable is destroyed: the server already has every
      // batch in here.
      confirmed.clear();
      const records = await store.all();
      // `owner === sessionId` as well as the in-memory set, and neither is
      // redundant: `send` stamps the record one awaited round-trip after adding
      // to `inflight`, so the two disagree for as long as that takes (as in
      // `reclaimStranded`).
      const held = records.filter((r) => r.state === INFLIGHT
        && (mine.has(r.batchId) || r.owner === sessionId));
      await Promise.all(held.map((r) => store.patch(
        r.batchId, { state: PENDING, notBefore: 0, unreachableFor: 0, ...NOT_INFLIGHT },
      )));
      [...waiting.keys()].forEach((batchId) => settleWaiter(batchId));
      // **Unknown rather than `held`.** This arm also settles every waiter and
      // clears three in-memory sets, so what changed is wider than the records
      // it patched, and naming only those would be an under-inclusive hint.
      announceUnknown();
    },

    /** Test seam, and the hard reset. Destroys what is owed — see `endSession`. */
    async reset() {
      // A hard reset is a queue with nothing in it and nothing owed, so it is
      // also not an ended session — leaving #298's flag set would carry a
      // sign-out across the seam that exists to remove one.
      ended = false;
      // Bumped first. A dispatch this session still has out cannot be cancelled
      // — nothing here owns the SDK's commit — so the only thing `reset` can do
      // about it is make its answer a no-op when it arrives. See `settleWith`.
      generation += 1;
      [...liveTimers].forEach(disarm);
      timer = null;
      timerAt = 0;
      heartbeat = null;
      waiting.clear();
      inflight.clear();
      reclaimed.clear();
      pending.clear();
      confirmed.clear();
      // The session's lock goes with the queue's state (#263). Only here: a
      // sign-out does not end the *tab*, and the lock means this tab is
      // alive.
      if (releaseLock) releaseLock();
      releaseLock = null;
      holdingLock = null;
      seq = null;
      // Cleared alongside `seq`, or the next `nextSeq` awaits an already-settled
      // promise whose handler has run, leaves `seq` null, and increments null.
      seqReady = null;
      await store.clear();
      // The whole queue is gone, which is every path there was.
      announceUnknown();
    },
  };
}
